// MEMBER AUDIT (docs/MEMBER-AUDIT-SPEC.md, 2026-07-13) — pure, DB-free rules
// for the account-less /a/[token] member flow: invite-state classification,
// step-data validation, load bands, the member-answer → canonical
// enjoyment_rating mapping, and the visibility-policy copy rendered on the
// consent screen. Everything here is tested (memberAudit.test.ts); the API
// routes and wizard components consume these so the boundary conditions
// live in one place — same philosophy as inviteLifecycle.ts.

import { isInviteExpired } from "./inviteLifecycle";

// The header the member client sends its invite token in (token IS the
// identity — no cookies). Lives here (pure module) so client components can
// import it without pulling the prisma-backed memberAuth server module.
export const INVITE_TOKEN_HEADER = "x-invite-token";

// ── Invite entry states ─────────────────────────────────────────────────

// What the /a/[token] entry sees. "invalid" is the caller's job (token
// resolved to no invite); this classifies a real invite row.
//   valid     — the member can (re)enter the wizard
//   completed — work is in; show the done screen (never expires)
//   expired   — past expires_at or already marked EXPIRED; friendly re-send screen
export type InviteEntryState = "valid" | "completed" | "expired";

export function classifyInvite(
  invite: { status: string; expires_at: Date },
  now: Date,
): InviteEntryState {
  if (invite.status === "COMPLETED") return "completed";
  if (invite.status === "EXPIRED" || isInviteExpired(invite, now)) return "expired";
  return "valid";
}

// ── Wizard steps ────────────────────────────────────────────────────────
// Persisted on DelegationSession.current_step so closing the phone and
// reopening the link resumes at the right screen. Consent is step 0 (never
// persisted — a session only exists after consent); DONE is terminal.
//
// ROLE-ADAPTIVE (R4): the step MANAGEMENT (the manager-only "your team &
// management" section) sits between CAPACITY and FRICTION. ICs skip it — the
// flow, count, and progress positions all come from the role-aware helpers
// below, never from a hard-coded next/prev, so the two branches can't drift.
export const MEMBER_STEP = {
  SEAT: 1, // Your seat (role title + clarity items)
  TASKS: 2, // What you actually do (guided list / brain-dump+chips)
  RATINGS: 3, // per-task hours + feelings + (guided) the "why"
  CAPACITY: 4, // hours in a typical week + load band
  MANAGEMENT: 5, // MANAGERS ONLY — team & management section (role_context)
  FRICTION: 6, // cracks & shadows free text
  DONE: 7,
} as const;

// The ordered steps a member actually walks (excludes DONE). Managers get the
// extra MANAGEMENT step; ICs don't — this single source drives count, the
// progress position, and forward/back navigation.
//
// DELTA SCANS (#9): a delta round is the 3-5 min prefilled re-audit. It DROPS
// the SEAT step (the full role-clarity battery) — the member's role clarity
// carries over from the prior session, and the one item worth re-checking
// (how often they get pulled off their own work) rides on the CAPACITY step.
// Everything else is the same converging shape, just prefilled: confirm the
// task list, adjust hours/ratings, re-answer load band + cracks (+ managers'
// short management read). isDelta=false is the full-round behavior, unchanged.
export function memberStepFlow(isManager: boolean, isDelta = false): number[] {
  const flow: number[] = [];
  if (!isDelta) flow.push(MEMBER_STEP.SEAT);
  flow.push(MEMBER_STEP.TASKS, MEMBER_STEP.RATINGS, MEMBER_STEP.CAPACITY);
  if (isManager) flow.push(MEMBER_STEP.MANAGEMENT);
  flow.push(MEMBER_STEP.FRICTION);
  return flow;
}

// What the member sees ("step x of N") — full: 5 IC / 6 manager; delta drops
// one (the SEAT step) off each.
export function memberStepCount(isManager: boolean, isDelta = false): number {
  return memberStepFlow(isManager, isDelta).length;
}

// 1-based position of a step within the member's flow (DONE → the last slot).
export function memberStepPosition(step: number, isManager: boolean, isDelta = false): number {
  const flow = memberStepFlow(isManager, isDelta);
  const i = flow.indexOf(step);
  return i === -1 ? flow.length : i + 1;
}

export function nextMemberStep(step: number, isManager: boolean, isDelta = false): number {
  const flow = memberStepFlow(isManager, isDelta);
  const i = flow.indexOf(step);
  if (i === -1 || i >= flow.length - 1) return MEMBER_STEP.DONE;
  return flow[i + 1] as number;
}

export function prevMemberStep(step: number, isManager: boolean, isDelta = false): number {
  const flow = memberStepFlow(isManager, isDelta);
  const i = flow.indexOf(step);
  if (i <= 0) return flow[0] as number;
  return flow[i - 1] as number;
}

// Resume rule: snap a persisted current_step onto the member's real flow (an
// IC whose row somehow says MANAGEMENT lands on the nearest earlier step;
// anything at/after DONE re-enters at the last real step). On a delta round
// a persisted SEAT (e.g. a full round the member never finished, reopened as
// delta) snaps forward to the first delta step.
export function resumeMemberStep(
  step: number | null | undefined,
  isManager: boolean,
  isDelta = false,
): number {
  const flow = memberStepFlow(isManager, isDelta);
  const s = clampMemberStep(step ?? flow[0]);
  if (s >= MEMBER_STEP.DONE) return flow[flow.length - 1] as number;
  if (flow.includes(s)) return s;
  // not on this role's flow (e.g. IC + MANAGEMENT, or delta + SEAT) →
  // nearest earlier step, else the first step of the flow
  const earlier = flow.filter((f) => f <= s);
  return (earlier.length ? earlier[earlier.length - 1] : flow[0]) as number;
}

export function clampMemberStep(step: unknown): number {
  const n = Number(step);
  if (!Number.isInteger(n)) return MEMBER_STEP.SEAT;
  return Math.min(Math.max(n, MEMBER_STEP.SEAT), MEMBER_STEP.DONE);
}

// ── Role derivation from the work chart (R4) ────────────────────────────
// isManager = the member owns direct reports (any Member.reports_to_id points
// at them) OR they are the owner-member. Pure so the invite route (which does
// the DB count) and the tests share one rule.
export function isManagerRole(input: {
  is_owner?: boolean;
  direct_report_count?: number;
}): boolean {
  return Boolean(input.is_owner) || (input.direct_report_count ?? 0) > 0;
}

// ── Delta prefill — prior-session selection (#9) ────────────────────────
// A delta round is a short re-audit prefilled from the member's most recent
// COMPLETED session for the SAME company on an EARLIER round. This picks that
// session from a candidate list (the caller runs the DB query; this pure rule
// decides which one and is unit-tested). "Most recent" = highest round.number,
// created_at as the tiebreak. Returns null when the member has no prior
// completed session — the delta round then falls back to the full audit.
export type PriorSessionCandidate = {
  id: string;
  status: string;
  round: { number: number } | null;
  created_at: Date | string;
};

export function pickPriorCompletedSession<T extends PriorSessionCandidate>(
  candidates: T[],
): T | null {
  const done = candidates.filter((s) => s.status === "COMPLETED");
  if (done.length === 0) return null;
  return done.reduce((best, s) => {
    const bn = best.round?.number ?? -1;
    const sn = s.round?.number ?? -1;
    if (sn !== bn) return sn > bn ? s : best;
    return new Date(s.created_at).getTime() > new Date(best.created_at).getTime() ? s : best;
  });
}

// Whether a delta round should actually run the SHORT prefilled experience:
// only when the round is delta-typed AND a prior completed session exists to
// prefill from. First-ever-for-this-member delta rounds fall back to full.
export function isDeltaExperience(roundType: string, hasPriorCompleted: boolean): boolean {
  return roundType === "delta" && hasPriorCompleted;
}

// ── Load bands (step 4) ─────────────────────────────────────────────────

export const LOAD_BANDS = ["underloaded", "comfortable", "full", "overloaded", "drowning"] as const;
export type LoadBand = (typeof LOAD_BANDS)[number];

export function isLoadBand(value: unknown): value is LoadBand {
  return typeof value === "string" && (LOAD_BANDS as readonly string[]).includes(value);
}

// Member-facing labels (6th-grade ESL rule — short, plain, no HR words).
export const LOAD_BAND_LABELS: Record<LoadBand, { label: string; hint: string }> = {
  underloaded: { label: "I could take on more", hint: "There's room in my week" },
  comfortable: { label: "It's a good amount", hint: "Busy, but it works" },
  full: { label: "My week is full", hint: "No room for anything new" },
  overloaded: { label: "It's too much", hint: "Things slip because there's no time" },
  drowning: { label: "I'm drowning", hint: "I can't keep up, most weeks" },
};

// ── Role clarity (step 2) ───────────────────────────────────────────────
// Four 5-point items, stored as DelegationSession.role_clarity Json:
//   { role_title, items: { responsibilities, success_definition,
//     pulled_into_others, handoff_clarity } }
// Note the polarity: pulled_into_others is "how OFTEN you get pulled into
// work that isn't yours" — 5 = constantly = LOW clarity. The clarity score
// (memberMiniReport.ts) inverts it.

export const ROLE_CLARITY_ITEMS = [
  "responsibilities",
  "success_definition",
  "pulled_into_others",
  "handoff_clarity",
] as const;
export type RoleClarityItem = (typeof ROLE_CLARITY_ITEMS)[number];

export type RoleClarity = {
  role_title: string;
  items: Record<RoleClarityItem, number>; // each 1..5
};

export type ValidationResult<T> = { ok: true; value: T } | { ok: false; error: string };

function asScale(value: unknown): number | null {
  const n = Number(value);
  if (!Number.isInteger(n) || n < 1 || n > 5) return null;
  return n;
}

export function validateRoleClarity(input: unknown): ValidationResult<RoleClarity> {
  if (typeof input !== "object" || input === null) {
    return { ok: false, error: "Role answers are missing" };
  }
  const raw = input as Record<string, unknown>;
  const role_title = typeof raw.role_title === "string" ? raw.role_title.trim().slice(0, 120) : "";
  if (!role_title) return { ok: false, error: "Tell us what your role is called" };

  const rawItems = (raw.items ?? {}) as Record<string, unknown>;
  const items = {} as Record<RoleClarityItem, number>;
  for (const key of ROLE_CLARITY_ITEMS) {
    const v = asScale(rawItems[key]);
    if (v === null) return { ok: false, error: "Please answer all four questions" };
    items[key] = v;
  }
  return { ok: true, value: { role_title, items } };
}

// ── Capacity (step 4) ───────────────────────────────────────────────────

export type Capacity = { capacity_hours: number; load_band: LoadBand };

export function validateCapacity(input: unknown): ValidationResult<Capacity> {
  if (typeof input !== "object" || input === null) {
    return { ok: false, error: "Capacity answers are missing" };
  }
  const raw = input as Record<string, unknown>;
  const hours = Number(raw.capacity_hours);
  if (!Number.isFinite(hours) || hours <= 0 || hours > 120) {
    return { ok: false, error: "Hours per week should be between 1 and 120" };
  }
  if (!isLoadBand(raw.load_band)) {
    return { ok: false, error: "Pick the option that matches your week" };
  }
  return {
    ok: true,
    value: { capacity_hours: Math.round(hours * 10) / 10, load_band: raw.load_band },
  };
}

// ── Friction / cracks & shadows (step 5) ────────────────────────────────
// Three free-text answers; each is optional individually but at least one
// must carry substance (the step powers overlap/gap detection — an all-empty
// submit means the member skipped the screen entirely).

export const FRICTION_FIELDS = ["cracks", "invisible", "drop"] as const;
export type FrictionField = (typeof FRICTION_FIELDS)[number];
export type Friction = Record<FrictionField, string>;

const FRICTION_MAX_CHARS = 2000;

export function validateFriction(input: unknown): ValidationResult<Friction> {
  if (typeof input !== "object" || input === null) {
    return { ok: false, error: "Answers are missing" };
  }
  const raw = input as Record<string, unknown>;
  const value = {} as Friction;
  for (const key of FRICTION_FIELDS) {
    const v =
      typeof raw[key] === "string" ? (raw[key] as string).trim().slice(0, FRICTION_MAX_CHARS) : "";
    value[key] = v;
  }
  if (!FRICTION_FIELDS.some((k) => value[k].length > 0)) {
    return { ok: false, error: "Give us at least one answer — even one line helps" };
  }
  return { ok: true, value };
}

// ── Per-task ratings (step 3) ───────────────────────────────────────────

// hours/week on one task: required, 0.5..80, one decimal of precision.
export function validateHoursPerWeek(value: unknown): number | null {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0 || n > 80) return null;
  return Math.round(n * 10) / 10;
}

// ── Per-task "why" (R1) — the goal-alignment signal ─────────────────────
// Every task carries a member-voiced reason it matters to the team/company
// goal. It is MANDATORY in the flow: guided asks it on each ratings card;
// ai-deep pulls it from the brain-dump and gap-fills what the AI missed.
// A soft minimum keeps out one-word throwaways ("job", "idk") while still
// letting a genuinely short real reason through — low-signal answers persist
// (the report low-weights them) but the copy nudges for a real reason.
export const WHY_MIN_CHARS = 12;
export const WHY_MAX_CHARS = 400;

// A one-line example shown under the prompt (company-goal framed, member voice).
export const WHY_EXAMPLE =
  'e.g. "Keeps our clients paid up on time, so cash doesn\'t stall the team."';

const WHY_JUNK =
  /^(idk|dunno|n\/?a|none|nothing|my job|just my job|because|it'?s my job|task)\.?$/i;

// Normalize + soft-validate a "why". Returns the trimmed value on success.
export function validateWhy(value: unknown): ValidationResult<string> {
  const raw = typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";
  if (!raw) {
    return { ok: false, error: "Add a reason this task matters to the team." };
  }
  if (raw.length < WHY_MIN_CHARS || WHY_JUNK.test(raw)) {
    return {
      ok: false,
      error: "Give a real reason — what goal or outcome does it serve?",
    };
  }
  return { ok: true, value: raw.slice(0, WHY_MAX_CHARS) };
}

// Whether a stored task_why already clears the bar (used to decide gap-fill).
export function isWhyFilled(why: string | null | undefined): boolean {
  return validateWhy(why).ok;
}

// AI-DEEP GAP-FILL: given the tasks after extraction, the ones still missing
// a usable "why" — the ONLY ones the fill-the-gaps step asks about (captured
// whys are never re-asked). Order is preserved so the pager is stable.
export function tasksNeedingWhy<T extends { task_why?: string | null }>(activities: T[]): T[] {
  return activities.filter((a) => !isWhyFilled(a.task_why));
}

// ── Manager "team & management" section → role_context (R4) ─────────────
// Managers answer a short extra block; ICs never see it and leave the field
// null. Kept tight (this is the manager's extra load). team_load_read reuses
// LOAD_BANDS so it lines up with the reports' own self-reported load band for
// the later triangulation lens.
export type RoleContext = {
  mgmt_share: number; // % of the week spent on management/oversight vs own work (0..100)
  team_load_read: LoadBand; // manager's read of the team's typical load
  team_clarity_read: number; // 1..5: how clear the manager thinks the team is on their work
  meeting_overhead_hours: number; // hrs/week in meetings + coordination
  bottleneck: string; // biggest team bottleneck (free text, optional)
};

const BOTTLENECK_MAX_CHARS = 1000;

export function validateRoleContext(input: unknown): ValidationResult<RoleContext> {
  if (typeof input !== "object" || input === null) {
    return { ok: false, error: "Management answers are missing" };
  }
  const raw = input as Record<string, unknown>;

  const share = Number(raw.mgmt_share);
  if (!Number.isFinite(share) || share < 0 || share > 100) {
    return { ok: false, error: "Pick roughly how much of your week is managing" };
  }
  if (!isLoadBand(raw.team_load_read)) {
    return { ok: false, error: "Give your read on the team's load" };
  }
  const clarity = asScale(raw.team_clarity_read);
  if (clarity === null) {
    return { ok: false, error: "Rate how clear the team is on their work" };
  }
  const overhead = Number(raw.meeting_overhead_hours);
  if (!Number.isFinite(overhead) || overhead < 0 || overhead > 80) {
    return { ok: false, error: "Set your meeting & coordination hours" };
  }
  const bottleneck =
    typeof raw.bottleneck === "string" ? raw.bottleneck.trim().slice(0, BOTTLENECK_MAX_CHARS) : "";

  return {
    ok: true,
    value: {
      mgmt_share: Math.round(share),
      team_load_read: raw.team_load_read,
      team_clarity_read: clarity,
      meeting_overhead_hours: Math.round(overhead * 10) / 10,
      bottleneck,
    },
  };
}

export const ENERGY_CHOICES = [
  { value: -2, label: "Drains me a lot" },
  { value: -1, label: "Drains me a bit" },
  { value: 0, label: "Neither way" },
  { value: 1, label: "Gives me energy" },
  { value: 2, label: "Lights me up" },
] as const;

export const SKILL_CHOICES = [
  { value: "Basic", label: "Still learning" },
  { value: "Intermediate", label: "I'm solid at it" },
  { value: "Advanced", label: "One of my best skills" },
] as const;

export const ENJOY_CHOICES = [
  { value: "love", label: "Love it" },
  { value: "like", label: "Like it" },
  { value: "meh", label: "Don't enjoy it" },
  { value: "dread", label: "Dread it" },
] as const;
export type EnjoyChoice = (typeof ENJOY_CHOICES)[number]["value"];

// Map the member's two answers (enjoyment + skill) onto the canonical
// 4-string enjoyment_rating the scoring engine already understands
// (delegationScoring.ts). The canonical strings pack enjoyment AND
// competence, so the "don't enjoy" half splits on the skill answer.
export function toEnjoymentRating(enjoy: EnjoyChoice, skill: string | null): string {
  if (enjoy === "love") return "Love / Great";
  if (enjoy === "like") return "Like / Good";
  return skill === "Basic" ? "Don't like / Not Good" : "Don't like / Good";
}

// ── Consent-screen copy helpers ─────────────────────────────────────────
// visibility_policy values are defined in app code (schema comment). The
// onboarding step 3 (copy doc §2.4) writes owner_only / personal_share /
// open_book; each maps to its own honest consent line here. Unknown or
// legacy values ("full", pre-onboarding rows) fall back to the
// full-visibility copy because that is the schema default and
// over-claiming privacy would lie.

export const VISIBILITY_POLICY_VALUES = ["owner_only", "personal_share", "open_book"] as const;
export type VisibilityPolicy = (typeof VISIBILITY_POLICY_VALUES)[number];

export function visibilitySummary(policy: string | null | undefined, ownerName: string): string {
  if (policy === "aggregate") {
    return `${ownerName} sees team-level patterns only — totals and overlaps across the whole team. Your individual answers are not shown with your name on them.`;
  }
  const base = `${ownerName} will see your answers — your task list, hours, energy ratings, and role answers — with your name on them. This is not anonymous, and we won't pretend it is.`;
  if (policy === "owner_only") {
    return `${base} All results go to ${ownerName} alone — nobody else on the team sees anything of yours.`;
  }
  if (policy === "personal_share") {
    return `${base} You also get your own private mini-report — nobody on the team sees anyone else's.`;
  }
  if (policy === "open_book") {
    return `${base} ${ownerName} may also share the team-level overview with everyone — the big picture, so the whole team sees how work is spread.`;
  }
  return base;
}
